import {
  HomeIcon,
  NavBarAdvancedApi,
  NavBarAnalytics,
  NavBarBilling,
  NavBarChainsList,
  NavBarEnterprise,
  NavBarProjects,
  NavBarSettings,
  NavBarTeams,
  NotificationBellIcon,
} from '@ankr.com/ui';
import { t } from '@ankr.com/common';

import { AccountPermission } from 'modules/permissions/constants';
import { AdvancedApiRoutesConfig } from 'domains/advancedApi/routes';
import {
  NavigationItem,
  NavigationNestedItem,
} from 'modules/common/components/Navigation/components/BaseNavButton';
import { UserSettingsRoutesConfig } from 'domains/userSettings/Routes';
import { billingRoutesConfig } from 'domains/account/routes';
import { chainsRoutesConfig } from 'domains/chains/routes';
import { homeRoutesConfig } from 'domains/home/routes';
import { notificationsRoutesConfig } from 'domains/notifications/routes';
import { platformSwaggerRoutesConfig } from 'domains/platformSwagger/routes';
import { projectsRoutesConfig } from 'domains/projects/routes';
import { statisticsRoutesConfig } from 'domains/statistics/routes';
import { teamsRoutesConfig } from 'domains/teams/routes';

import { IMenuItemsParams } from '../types';

const checkIsChainsRoute = (pathname: string, chainsRoutes: string[]) => {
  return chainsRoutes.some(route => pathname.includes(route));
};

/* eslint-disable max-lines-per-function */
export const getTopMenuItems = ({
  chainsRoutes,
  currentPath,
  isEnterpriseClient,
  isLoggedIn,
  isMobileSideBar,
  isPartnerClient,
  isPersonalGroup,
  isWeb3Auth,
  isXsDown,
  notificationsAmount,
  onOpenAccessDeniedDialog,
  onOpenUpgradePlanDialog,
  teamInvitationsAmount,
}: IMenuItemsParams): NavigationItem[] => {
  const personalSettingsNestedItems: NavigationNestedItem[] = [
    {
      title: '', // empty string fixes a warning about a unique key
      items: [
        {
          href: UserSettingsRoutesConfig.settings.generatePath(),
          label: t('main-navigation.platform'),
          requiresPermission: AccountPermission.CommonMenuItem,
        },
        {
          href: platformSwaggerRoutesConfig.platformSwagger.generatePath(),
          isHidden: isWeb3Auth,
          label: t('main-navigation.platform-swagger'),
          requiresPermission: AccountPermission.CommonMenuItem,
        },
      ],
    },
    {
      title: t('main-navigation.personal-account'),
      items: [
        {
          href: UserSettingsRoutesConfig.notifications.generatePath(),
          label: t('main-navigation.notifications'),
          requiresPermission: AccountPermission.CommonMenuItem,
        },
      ],
    },
  ];

  const teamSettingsNestedItems: NavigationNestedItem[] = [
    {
      title: '', // empty string fixes a warning about a unique key
      items: [
        {
          href: UserSettingsRoutesConfig.settings.generatePath(),
          label: t('main-navigation.platform'),
          requiresPermission: AccountPermission.CommonMenuItem,
        },
        {
          href: platformSwaggerRoutesConfig.platformSwagger.generatePath(),
          isHidden: isWeb3Auth,
          label: t('main-navigation.platform-swagger'),
          requiresPermission: AccountPermission.CommonMenuItem,
        },
      ],
    },
    {
      title: t('main-navigation.team-account'),
      items: [
        {
          href: UserSettingsRoutesConfig.teamGeneral.generatePath(),
          label: t('main-navigation.team-general'),
          requiresPermission: AccountPermission.CommonMenuItem,
        },
        {
          href: UserSettingsRoutesConfig.teammates.generatePath(),
          label: t('main-navigation.teammates'),
          requiresPermission: AccountPermission.CommonMenuItem,
        },
        {
          href: UserSettingsRoutesConfig.teamNotifications.generatePath(),
          isDisabled: isPartnerClient,
          label: t('main-navigation.team-notifications'),
          requiresPermission: AccountPermission.CommonMenuItem,
        },
      ],
    },
  ];

  const items: NavigationItem[] = [
    {
      StartIcon: HomeIcon,
      // should be active on the news page too
      isActive: currentPath.includes(homeRoutesConfig.home.generatePath()),
      isClickableWhenActive:
        currentPath === homeRoutesConfig.news.generatePath(),
      isDisabled: isPartnerClient,
      isHidden: !isLoggedIn,
      href: homeRoutesConfig.home.generatePath(),
      label: t('main-navigation.home'),
      requiresPermission: undefined,
    },
    {
      StartIcon: NavBarChainsList,
      href: chainsRoutesConfig.chains.generatePath(),
      isActive: checkIsChainsRoute(currentPath, chainsRoutes),
      isDisabled: isPartnerClient,
      isHidden: false,
      label: t('main-navigation.chains-list'),
      onAccessDeniedClick: onOpenAccessDeniedDialog,
      requiresPermission: AccountPermission.ChainItem,
    },
    {
      StartIcon: NavBarProjects,
      href: projectsRoutesConfig.projects.generatePath(),
      isDisabled: isPartnerClient,
      isHidden: false,
      label: t('main-navigation.projects'),
      onAccessDeniedClick: onOpenAccessDeniedDialog,
      requiresPermission: AccountPermission.ProjectsMenuItem,
    },
    {
      StartIcon: NavBarAnalytics,
      href: statisticsRoutesConfig.statistics.generatePath(),
      isDisabled: false,
      isHidden: isXsDown,
      label: t('main-navigation.analytics'),
      onAccessDeniedClick: onOpenAccessDeniedDialog,
      requiresPermission: AccountPermission.AnalyticsMenuItem,
    },
    {
      StartIcon: NavBarAdvancedApi,
      href: AdvancedApiRoutesConfig.advancedApi.generatePath(),
      isDisabled: isPartnerClient,
      label: t('main-navigation.advanced-api'),
      onAccessDeniedClick: onOpenAccessDeniedDialog,
      requiresPermission: AccountPermission.AdvancedApiMenuItem,
    },
    {
      StartIcon: NavBarTeams,
      amount: teamInvitationsAmount,
      href: teamsRoutesConfig.teams.generatePath(),
      isHidden: !isLoggedIn,
      label: t('main-navigation.teams'),
      requiresPermission: AccountPermission.CommonMenuItem,
    },
    {
      StartIcon: NavBarSettings,
      internalItems: isPersonalGroup
        ? personalSettingsNestedItems
        : teamSettingsNestedItems,
      isHidden: !isLoggedIn,
      label: t('main-navigation.settings'),
      onAccessDeniedClick: onOpenAccessDeniedDialog,
      requiresPermission: AccountPermission.CommonMenuItem,
    },
    {
      StartIcon: NotificationBellIcon,
      amount: notificationsAmount,
      href: notificationsRoutesConfig.notifications.generatePath(),
      isDisabled: isPartnerClient,
      isHidden: !isLoggedIn || !isMobileSideBar,
      label: t('main-navigation.notifications'),
      requiresPermission: AccountPermission.CommonMenuItem,
    },
    {
      StartIcon: NavBarBilling,
      href: billingRoutesConfig.billing.generatePath(),
      isDisabled: isPartnerClient,
      isHidden: !isLoggedIn || isEnterpriseClient,
      label: t('main-navigation.billing'),
      onAccessDeniedClick: onOpenAccessDeniedDialog,
      requiresPermission: AccountPermission.Billing,
    },
    {
      StartIcon: NavBarEnterprise,
      href: '/enterprise', // this string is used for compatibility. not used as route
      isHidden: isEnterpriseClient || isPartnerClient,
      isNotLinkItem: true,
      label: t('main-navigation.enterprise'),
      onAccessDeniedClick: onOpenUpgradePlanDialog,
      onClick: (
        event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
      ) => {
        // we use this menu item just for opening the dialog. so we need to prevent the default behavior
        event.preventDefault();
        onOpenUpgradePlanDialog();
      },
      requiresPermission: AccountPermission.CommonMenuItem,
    },
  ];

  return items.filter(item => !item.isHidden);
};
